import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { recoverFromStaleChunk, isStaleChunkError, showStaleChunkFallback } from '@ams/ui-kit';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService implements ErrorHandler {
  private readonly _router = inject(Router);

  handleError(error: unknown): void {
    // A chunk removed by a deploy is not an application fault — the running tab is
    // simply out of date. Reload onto the current build instead of sending the
    // user to /error, which is a dead end they can only escape by reloading anyway.
    if (recoverFromStaleChunk(error)) return;

    if (isStaleChunkError(error)) {
      // Recovery is exhausted — a reload already happened and the chunk is still
      // unservable. Do NOT fall through to /error: that route is itself lazily
      // loaded, so navigating there during a chunk incident fails the same way,
      // and the router's rejection comes straight back to this handler through
      // provideBrowserGlobalErrorListeners' unhandledrejection listener. That is
      // an endless navigate-fail-navigate loop. Log and stop instead.
      // This can arrive from bootstrapApplication's initial navigation, before any
      // component is mounted, so there is nothing on screen to show a message
      // through — logging alone leaves the user on a blank white page.
      console.error('Stale chunk after a reload already failed to recover:', error);
      showStaleChunkFallback();
      return;
    }

    // Log the error to a service like Sentry or Azure Monitor
    console.error('Captured Error:', error);

    // Extract error message and details
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    this._router.navigate(['/error'], {
      state: {
        error: {
          message: errorMessage
        }
      }
    });
  }
}
